import { onBeforeUnmount, ref, watch } from 'vue'
import { useRoute, useRouter, type LocationQuery } from 'vue-router'
import { useToastStore } from '@/stores/toast'
import { fetchJob } from '@/services/entities/job'
import { JobStatus } from '@/enums/JobStatus'
import { QES_POPUP_WINDOW_NAME, QES_RETURN_CHANNEL_NAME } from '@/helpers/qes-popup-helper'

export type QesReturnOutcome =
  | 'signed'
  | 'signing-failed'
  | 'signing-cancelled'
  | 'signing-partial'
  | 'signing-in-progress'
  | 'signing-superseded'
  | 'connected'
  | 'connect-failed'

type QesReturn = { outcome: QesReturnOutcome; at: number }

const OUTCOMES: QesReturnOutcome[] = [
  'signed',
  'signing-failed',
  'signing-cancelled',
  'signing-partial',
  'signing-in-progress',
  'signing-superseded',
  'connected',
  'connect-failed'
]

// How long after a `signed` return the signing UI keeps checking whether the
// asynchronous processing (QES store -> next job, via Messenger) has moved the
// signing job on, and how often.
const SIGNED_REFRESH_WINDOW_MS = 60 * 1000
const SIGNED_REFRESH_INTERVAL_MS = 5 * 1000

// The latest outcome this tab has seen — its own landing or one broadcast by
// the popup. A ref rather than an event, so signing components mounted after it
// arrived (e.g. the cart still loading after a same-tab redirect) still see it.
export const lastQesReturn = ref<QesReturn | null>(null)

// The terminal redirects of the Cleverbase chains: `?qes=success|failed|cancelled|partial|in_progress|superseded`
// from signing (CleverbaseSigningCredentialHopHandler / CleverbaseSigningFailureHandler;
// `cancelled` = the login popup was cancelled, job unchanged; `partial` = signatures
// issued but some PDFs not finished yet, job stays in realization; `in_progress` = a
// duplicate callback (reloaded popup) while another request is still signing — its
// outcome arrives like a `success` one, by refreshing the job; `superseded` = a callback of
// an earlier attempt, ignored while the latest one continues),
// `?cleverbase=connected|error` from connecting (CleverbaseConnectHandler).
const outcomeFromQuery = (query: LocationQuery): QesReturnOutcome | null => {
  if (query.qes === 'success') return 'signed'
  if (query.qes === 'failed') return 'signing-failed'
  if (query.qes === 'cancelled') return 'signing-cancelled'
  if (query.qes === 'partial') return 'signing-partial'
  if (query.qes === 'in_progress') return 'signing-in-progress'
  if (query.qes === 'superseded') return 'signing-superseded'
  if (query.cleverbase === 'connected') return 'connected'
  if (query.cleverbase === 'error') return 'connect-failed'

  return null
}

/**
 * Used once, from App.vue: turns a Cleverbase return param into a toast, tells
 * every other backoffice tab about it (the PM's original tab, where "Sign now"
 * was clicked, is a different tab than the popup that lands here), strips the
 * param so a reload doesn't repeat it, and closes the popup it landed in.
 */
export const useQesReturnNotice = () => {
  const route = useRoute()
  const router = useRouter()
  const toastStore = useToastStore()

  // BroadcastChannel never delivers a message back to the instance that posted
  // it, so with one instance per tab the sending tab doesn't toast twice.
  const channel =
    typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(QES_RETURN_CHANNEL_NAME) : null

  const notify = (outcome: QesReturnOutcome) => {
    switch (outcome) {
      case 'signed':
        toastStore.info(
          "The document was signed successfully. Processing continues in the background — the job will move on automatically in a moment; refresh the page if it doesn't update.",
          'Signature completed'
        )
        break
      case 'signing-failed':
        toastStore.error(new Error('Signing failed. The job was reset — please try again.'))
        break
      case 'signing-cancelled':
        toastStore.info(
          'Signing was cancelled — the job is unchanged. You can sign again whenever you are ready.',
          'Signing cancelled'
        )
        break
      case 'signing-partial':
        toastStore.warn(
          'Your signature was issued, but some files could not be finished yet. Use “Finish signing” to complete them.'
        )
        break
      case 'signing-in-progress':
        toastStore.info(
          'Signing is still being processed — the page will update when it finishes.',
          'Signing in progress'
        )
        break
      case 'signing-superseded':
        toastStore.info(
          'This signing window belonged to an earlier attempt and was ignored; the latest attempt continues.',
          'Signing attempt superseded'
        )
        break
      case 'connected':
        toastStore.success('Your Cleverbase identity is connected. You can sign now.')
        break
      case 'connect-failed':
        toastStore.error(new Error('Connecting your Cleverbase identity failed. Please try again.'))
        break
    }
  }

  const record = (outcome: QesReturnOutcome) => {
    notify(outcome)

    // JobSigningForm.vue would otherwise stop awaiting the latest attempt.
    if (outcome !== 'signing-superseded') {
      lastQesReturn.value = { outcome, at: Date.now() }
    }
  }

  if (channel) {
    channel.onmessage = (event: MessageEvent) => {
      const outcome = (event.data as { outcome?: QesReturnOutcome } | null)?.outcome

      if (outcome && OUTCOMES.includes(outcome)) {
        record(outcome)
      }
    }
  }

  const handleQuery = () => {
    const outcome = outcomeFromQuery(route.query)
    if (!outcome) {
      return
    }

    const isPopup = window.name === QES_POPUP_WINDOW_NAME

    if (isPopup) {
      // About to close — only the toast, no refresh work in this window.
      notify(outcome)
    } else {
      record(outcome)
    }

    channel?.postMessage({ outcome })

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { qes, cleverbase, ...rest } = route.query
    router.replace({ query: rest, hash: route.hash })

    if (isPopup) {
      setTimeout(() => window.close(), 1500)
    }
  }

  router.isReady().then(() => {
    watch(() => [route.query.qes, route.query.cleverbase], handleQuery, { immediate: true })
  })

  onBeforeUnmount(() => channel?.close())
}

// One poll per signing job across components — the same job can be shown by
// more than one signing UI at once (order header button + job tab). Settled
// polls are remembered per return, so remounting a signing UI (switching tabs)
// doesn't poll the same job again for the same return.
const refreshingJobs = new Set<string>()
const settledJobs = new Map<string, number>()

/**
 * For the signing UIs: after a `signed` (or `signing-in-progress`) return, re-checks the signing job
 * every few seconds (for up to a minute) until the asynchronous processing has
 * moved it out of realization, then calls `onAdvanced` once to reload it. After
 * a `signing-failed` return the job was already reset, so it reloads right away.
 * A `signing-superseded` return never reaches `lastQesReturn`, so it is ignored.
 *
 * `refreshUntilAdvanced` runs the same check on demand — for a decline, whose
 * reset (SigningJobStrategy::reset() via Messenger) is just as asynchronous.
 * It polls `job` (defaults to `signingJob()`) and resolves `true` once it moved
 * on (after `onAdvanced`), or `false` if the window ran out / the poll was
 * stopped / nothing could be polled.
 */
export const useQesSigningRefresh = (
  signingJob: () => JobEntity | null | undefined,
  onAdvanced: () => unknown
) => {
  let timer: ReturnType<typeof setTimeout> | undefined
  let activeJobIri: string | null = null
  let resolveActive: ((advanced: boolean) => void) | null = null

  const stop = (advanced = false) => {
    clearTimeout(timer)
    timer = undefined

    if (activeJobIri) {
      refreshingJobs.delete(activeJobIri)
      activeJobIri = null
    }

    resolveActive?.(advanced)
    resolveActive = null
  }

  const settle = (jobIri: string, since: number, advanced = false) => {
    settledJobs.set(jobIri, since)
    stop(advanced)
  }

  const start = (
    since: number,
    job: JobEntity | null | undefined = signingJob()
  ): Promise<boolean> => {
    const jobIri = job?.['@id']

    if (!job || !jobIri || job.status !== JobStatus.REALIZATION) {
      return Promise.resolve(false)
    }

    if (refreshingJobs.has(jobIri) || settledJobs.get(jobIri) === since) {
      return Promise.resolve(false)
    }

    stop()
    activeJobIri = jobIri
    refreshingJobs.add(jobIri)

    const tick = async () => {
      try {
        const fresh = await fetchJob(jobIri)

        // stopped (unmounted / superseded) while the request was in flight
        if (activeJobIri !== jobIri) {
          return
        }

        if (fresh?.status !== JobStatus.REALIZATION) {
          // reload first, so a caller awaiting refreshUntilAdvanced only
          // drops its "please wait" state once the fresh job is shown
          try {
            await onAdvanced()
          } finally {
            settle(jobIri, since, true)
          }

          return
        }
      } catch {
        // transient — keep checking until the window runs out
        if (activeJobIri !== jobIri) {
          return
        }
      }

      if (Date.now() - since >= SIGNED_REFRESH_WINDOW_MS) {
        settle(jobIri, since)

        return
      }

      timer = setTimeout(tick, SIGNED_REFRESH_INTERVAL_MS)
    }

    timer = setTimeout(tick, SIGNED_REFRESH_INTERVAL_MS)

    return new Promise<boolean>((resolve) => (resolveActive = resolve))
  }

  const refreshUntilAdvanced = (job?: JobEntity | null) => start(Date.now(), job ?? signingJob())

  watch(
    lastQesReturn,
    (qesReturn, previous) => {
      if (!qesReturn || Date.now() - qesReturn.at >= SIGNED_REFRESH_WINDOW_MS) {
        return
      }

      // in progress: another request is still signing — wait it out the same way
      if (qesReturn.outcome === 'signed' || qesReturn.outcome === 'signing-in-progress') {
        // only Cleverbase signs through the popup that reports this return
        if (signingJob()?.qesProvider === 'cleverbase') {
          start(qesReturn.at)
        }
      } else if (
        // failed: the job was reset; partial: it now offers "Finish signing"
        (qesReturn.outcome === 'signing-failed' || qesReturn.outcome === 'signing-partial') &&
        // only a live update — a component mounted afterwards already
        // loaded the reset job
        previous !== undefined &&
        signingJob()
      ) {
        onAdvanced()
      }
    },
    { immediate: true }
  )

  onBeforeUnmount(() => stop())

  return { refreshUntilAdvanced }
}
